import { CssBaseline } from '@mui/material';
import { ELinks, EWebsocketTopics } from '@sharedInterfaces/globalEnums';
import {
    EGeneralWebsocketResponseType,
    type IGeneralWebsocketResponseMap,
    type TGeneralWebsocketResponse,
} from '@sharedInterfaces/websocket/General';
import getWhoIAm from '@src/APIs/graphQl/Employee/getWhoIAm';
import type { CustomGraphQLError } from '@src/APIs/graphQl/graphQL';
import { WebSocketClient } from '@src/APIs/WebSockets/WebSocketClient';
import { DialogContainer } from '@src/App/Components/Dialogs/DialogManager';
import { stageContext } from '@src/globals';
import { isLoggedIn } from '@store/reducer/clientReducer';
import { setCompanyAllEmployees, setCompanyStyle } from '@store/reducer/companyReducer';
import {
    setEmployeeAvailability,
    setEmployeeDescription,
    setEmployeeGender,
    setEmployeeNames,
    setEmployeePhoneNumber,
    setEmployeePictureId,
} from '@store/reducer/employeeReducer';
import type { AppState } from '@store/store';
import React, { useCallback, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useDispatch, useSelector } from 'react-redux';
import { BrowserRouter, useLocation } from 'react-router-dom';
import { logout } from './Components/Buttons/UserButton/UserButton';
import InnerLayout from './Components/Core/Layouts/InnerLayout/InnerLayout';
import Layout from './Components/Core/Layouts/MainLayout/MainLayout';
import { NavigationProvider } from './Components/Core/Navigation/NavigationContext';
import ErrorBoundary from './Components/Error/ErrorBoundary';
import ErrorPage from './Components/Error/ErrorPage';
import FatalErrorPage from './Components/Error/FatalErrorPage';
import CookieBanner from './Components/General/CookieBanner/CookieBanner';
import LoadingBox from './Components/General/LoadingBox/LoadingBox';
import RoutesComponent from './Routes';
import { ThemeProvider } from './Theme/ThemeContext';

const topic = EWebsocketTopics.GENERAL;

// Component to scroll to top on route change
function ScrollToTop() {
    const { pathname } = useLocation();

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [pathname]);

    return null;
}

function App() {
    const dispatch = useDispatch();
    const secret = useSelector((state: AppState) => state.client.secret);
    const startPage = useSelector((state: AppState) => state.employeeSettings.startPage);

    const loadWhoIAm = () => {
        if (isLoggedIn(secret)) {
            getWhoIAm(() => {}).catch((error: CustomGraphQLError) => {
                console.error(error);
                if (error.statusCode === 401) {
                    logout();
                }
            });
        }
    };

    const openWebSocket = () => {
        if (!WebSocketClient.socket) WebSocketClient.connect();
    };

    // Employee Update Handler
    const currentEmployeeId = useSelector((state: AppState) => state.employee.id);
    const allEmployees = useSelector((state: AppState) => state.company.allEmployees);

    const handleEmployeeUpdate = useCallback(
        (updatedEmployee: IGeneralWebsocketResponseMap[EGeneralWebsocketResponseType.EmployeeUpdate]['employee']) => {
            // 1. Eigene Daten aktualisieren
            if (updatedEmployee.id === currentEmployeeId) {
                dispatch(
                    setEmployeeNames({
                        firstname: updatedEmployee.firstName,
                        lastname: updatedEmployee.lastName,
                    }),
                );

                dispatch(setEmployeeGender(updatedEmployee.gender));

                if (updatedEmployee.phoneNumber !== undefined) {
                    dispatch(setEmployeePhoneNumber(updatedEmployee.phoneNumber));
                }

                if (updatedEmployee.availability) {
                    dispatch(setEmployeeAvailability(updatedEmployee.availability));
                }

                if (updatedEmployee.pictureId !== undefined) {
                    dispatch(setEmployeePictureId(updatedEmployee.pictureId));
                }

                if (updatedEmployee.description !== undefined) {
                    dispatch(setEmployeeDescription(updatedEmployee.description));
                }
            }

            // 2. AllEmployees aktualisieren
            const updatedAllEmployees = allEmployees.map((emp) =>
                emp.id === updatedEmployee.id
                    ? {
                          ...emp,
                          title: updatedEmployee.title,
                          email: updatedEmployee.email,
                          availability: updatedEmployee.availability || emp.availability,
                          pictureId: updatedEmployee.pictureId,
                      }
                    : emp,
            );
            dispatch(setCompanyAllEmployees(updatedAllEmployees));
        },
        [currentEmployeeId, allEmployees, dispatch],
    );

    React.useEffect(() => {
        const subKey = WebSocketClient.subscribe<TGeneralWebsocketResponse>(
            topic,
            (data: TGeneralWebsocketResponse) => {
                console.log(data);
                switch (data.type) {
                    case EGeneralWebsocketResponseType.Style:
                        console.log(data.style);
                        dispatch(setCompanyStyle(data.style));
                        break;
                    case EGeneralWebsocketResponseType.EmployeeUpdate:
                        handleEmployeeUpdate(data.employee);
                        break;
                    default:
                        // TYPESCRIPT FIX: TypeScript exhaustiveness checking requires explicit any cast
                        // PROBLEM: TypeScript couldn't determine data type in default case (type never)
                        // SOLUTION: Cast to any to access .type property for error logging
                        // SMALL CHANGE: Single (data as any) cast fixes compilation error
                        console.error('Unknown websocket response type', (data as any).type);
                        break;
                }
            },
        );

        return () => {
            return WebSocketClient.unsubscribe(subKey);
        };
    }, [handleEmployeeUpdate]);

    useEffect(() => {
        if (!secret || secret == null || secret === '') return;
        openWebSocket();
        const intervalId = setInterval(
            () => {
                loadWhoIAm();
            },
            2 * 60 * 60 * 1000,
        );
        loadWhoIAm();
        return () => {
            clearInterval(intervalId);
        };
    }, [secret]);

    if (startPage && location.pathname === '/') {
        location.pathname = ELinks[startPage];
    }
    return (
        <ErrorBoundary fallback={(error) => <FatalErrorPage error={error} />}>
            <Helmet>
                <title>{stageContext.productName}</title>
            </Helmet>
            <CssBaseline />
            <ThemeProvider>
                <NavigationProvider>
                    <BrowserRouter>
                        <ScrollToTop />
                        <DialogContainer />
                        <CookieBanner />
                        <Layout>
                            <ErrorBoundary fallback={(error) => <ErrorPage error={error} />}>
                                <div id="dialogContainer"></div>
                                {/* //TODO: Prüfen ob obsolet */}
                                <React.Suspense
                                    fallback={
                                        <InnerLayout>
                                            <LoadingBox />
                                        </InnerLayout>
                                    }
                                >
                                    <RoutesComponent />
                                </React.Suspense>
                            </ErrorBoundary>
                        </Layout>
                    </BrowserRouter>
                </NavigationProvider>
            </ThemeProvider>
        </ErrorBoundary>
    );
}

export default App;
